import { useCallback, useState } from 'react'
import { HallType } from '@weblite/ts-action/hall'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  HallCallTime,
  HallCallType,
  showChooseHallCallTimeDialog,
} from '$comp/modals/create-hall-call/choose-call-time'
import { showChooseChannelCallDialog } from '$comp/modals/create-hall-call/choose-call-type/choose-channel-call'
import { callService, getDefaultCallTitle } from '$logic/call'
import { useRoomAttribute } from '$logic/room'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { showChooseGroupCallDialog } from '$comp/modals/create-hall-call/choose-call-type/choose-group-call'
import {
  useHall,
  useRoomCanMakeOnlineClass,
  useRoomIsEducational,
} from '$logic/hall/react-hook'
import hostConf from '$config/frontend'

interface Props {
  roomId: string
  hallType: HallType
}

const hasCallOption = hostConf.HAS_CALL_OPTION

export function useStartCall({ roomId, hallType }: Props) {
  const [startingCall, setStartingCall] = useState(false)
  const call = useRoomAttribute('call', roomId)
  const userPermissions = useClientUserPermissions(roomId)
  const canStartCall = userPermissions?.isAdmin || userPermissions?.isOwner
  const { canMakeOnlineClass } = useRoomCanMakeOnlineClass(roomId)
  const { roomIsEducational } = useRoomIsEducational(roomId)
  const { entity: hall } = useHall(roomId)

  const openChooseCallTimeDialog = useCallback(
    async (callType: HallCallType, onBack: () => unknown) => {
      const defaultTitle = getDefaultCallTitle(hall?.name || '', callType)
      const { time, shouldSendNotificationMessage, title, shouldStartNow } =
        (await showChooseHallCallTimeDialog({
          callType,
          onBack,
          // hallType,
          titlePlaceholder: defaultTitle,
        })) as HallCallTime
      setStartingCall(true)
      if (shouldStartNow) {
        callService
          .startCall(roomId, hallType, callType, title || defaultTitle)
          .finally(() => {
            setStartingCall(false)
          })
      } else
        callService
          .scheduleCall({
            roomId,
            roomType: hallType,
            callType,
            callTime: time,
            name: title || defaultTitle,
            shouldSendNotification: shouldSendNotificationMessage,
          })
          .finally(() => {
            setStartingCall(false)
          })
    },
    [roomId, hallType, hall],
  )

  const openChooseCallDialog = useCallback(async () => {
    if ((!hasCallOption && canMakeOnlineClass) || roomIsEducational) {
      openChooseCallTimeDialog('classroom', async () => {
        await closeCurrentModal()
      })
      return
    }

    const callType =
      hallType === 'CHANNEL'
        ? await showChooseChannelCallDialog(canMakeOnlineClass)
        : await showChooseGroupCallDialog()

    openChooseCallTimeDialog(callType, async () => {
      await closeCurrentModal()
      openChooseCallDialog()
    })
  }, [
    hallType,
    openChooseCallTimeDialog,
    canMakeOnlineClass,
    roomIsEducational,
  ])

  return { openChooseCallDialog, startingCall, call, canStartCall }
}
